/* eslint-disable  @typescript-eslint/no-this-alias */
import user from './user';
import h, { getAvodConfiguration, getCountry } from './common';
import player from './bitmovinPlayer';
import url from './url';
import { SmartLib, LoggerManager, PlayerEventListener, StreamingSessionOptions } from '@broadpeak/smartlib';
import '@broadpeak/smartlib-analytics';
import '@broadpeak/smartlib-ad';
import '@broadpeak/smartlib-bitmovin';
import { PlayerAPI, PlayerEvent } from 'bitmovin-player';
import { AttachPlayerEventsCallback } from '../features/player';
import npawPlugin from '../singletons/npawPluginSingleton';
import type { ILocalStorageConfigurationObject } from '../types/ILocalStorage';
import { isApiNotAvailableError, isPlayerAlive, markPlayerDestroyed } from './playerGuards';
import { CDN, SMARTLIB_LOG_LEVEL } from './cdn/constants';
import { retryN } from './cdn/retry';
import { IAdEvents, IAdBreakData, IAdData } from './cdn/types';
import { playerSSAIManager } from '../utils/SSAI';
import {
    cdnLog,
    describePlayer,
    describeSession,
    registerCdnDebugState,
    noteSessionStopped,
    rememberSessionToken,
    wasSessionStopped,
    shortUrl,
} from './cdn/debug';

// import {
//     SmartLib as SmartLibType,
//     LoggerManager as LoggerManagerType,
//     PlayerEventListener as PlayerEventListenerType,
//     StreamingSessionOptions as StreamingSessionOptionsType,
// } from '@broadpeak/smartlib';

// let SmartLib: typeof SmartLibType,
//     LoggerManager: typeof LoggerManagerType,
//     PlayerEventListener: typeof PlayerEventListenerType,
//     StreamingSessionOptions: typeof StreamingSessionOptionsType;

/** How long a displaced session may wait for its player to attach before it is treated as abandoned. */
const RETIRED_UNBOUND_TTL_MS = 60_000;

function smartlibLogger(n: number): void {
    void n;
}

async function loadSmartLib(): Promise<void> {
    //const smartLib = await import(/* webpackChunkName: "@broadpeak/smartlib-v3" */ '@broadpeak/smartlib');
    //SmartLib = smartLib.SmartLib;
    //LoggerManager = smartLib.LoggerManager;
    // PlayerEventListener = smartLib.PlayerEventListener;
    //StreamingSessionOptions = smartLib.StreamingSessionOptions;
}

const cdnHandler = {
    config: null as any,
    avodConfig: null as ILocalStorageConfigurationObject['avodConfigs'],
    _isAdsEnabled: false,
    _player: null as PlayerAPI,
    _session: null as any,
    _initialized: false,
    _cdnName: '',
    _requestUrl: '',
    _resolvedsmartlibUrl: '',
    _cdnAdsUrlParams: [],
    _numberOfSessions: 0,
    /**
     * Sessions that a newer `initiateSession` displaced while another player could still be
     * streaming from them, keyed by session, with the player bound to each (null while its owner is
     * still resolving). Each stays running until its own player stops it (`stopCDNSessionFor`) or
     * is gone (`stopCDNSessionIfPlayerGone`), so one player starting never tears down another's.
     */
    _retired: new Map<any, { player: PlayerAPI | null; since: number }>(),
    /** Sessions their owner stopped on purpose; resolves still queued for them are skipped. */
    _abandoned: new WeakSet<object>(),
    /** Playback-event bindings per player (`bindEvents`), so binding one player never unbinds another. */
    _boundEvents: new Map<PlayerAPI, () => void>(),
    /** Keepalive frequency last applied to SmartLib, so the global option is only set when it changes. */
    _appliedKeepaliveFrequency: null as number | null,
    _resolveQueue: Promise.resolve(),
    _lowLatencyParams: '',
    /**
     * Resolves a URL using SmartLib CDN handling
     * @param streamUrl - The original URL to resolve
     * @param token - Optional authentication token
     * @param session - The caller's own session (from `initiateSession`). The URL is resolved on it,
     * never on whichever session happens to be current, and no session is created for the caller if
     * it has been stopped meanwhile (the caller unmounted or moved on while queued).
     * @returns The resolved URL from CDN or the original URL as fallback
     */
    async _resolveUrlWithQueue(
        streamUrl: string,
        token?: string,
        callback?: () => void,
        session?: unknown
    ): Promise<string> {
        // Handle invalid cases
        const resolution = this._resolveQueue.then(async () => {
            if (!streamUrl) return '';
            const { retryAttempt, retryDelay, enabled } = this.config || {};
            if (!enabled) return streamUrl;
            // The owner stopped its session while this resolve was queued (it unmounted or moved
            // on): nobody will play the result, so do not open a session for it.
            if (session && this._abandoned.has(session)) {
                cdnLog('resolve skipped: owner stopped its session while queued', {
                    session: describeSession(session),
                    url: shortUrl(streamUrl),
                });
                return streamUrl;
            }
            // Stopped for another reason (an earlier resolve on it failed): resolve as before.
            if (session && !this.isSessionAlive(session)) {
                cdnLog('resolve: own session already stopped, using a fresh one', {
                    session: describeSession(session),
                });
                session = undefined;
            }

            // Ensure session exists
            // if (!this._session) {
            //     this.initiateSession();
            //     callback?.();
            // }
            if (!session && !this.isSessionAlive(this._session)) {
                cdnLog('resolve: no live session, starting one', { url: shortUrl(streamUrl) });
                this.stopCDNSession('replaced by resolve');
                this.initiateSession();
                callback?.();
            }

            // Prepare URL with required parameters
            const parsedUrl = new URL(streamUrl);
            if (token) {
                parsedUrl.searchParams.set('token', token);
            }

            if (this._lowLatencyParams) {
                new URLSearchParams(this._lowLatencyParams).forEach((value, key) => {
                    parsedUrl.searchParams.set(key, value);
                });
            }

            if (this._isAdsEnabled && this._cdnAdsUrlParams && this._cdnAdsUrlParams.length > 0) {
                // parsedUrl.searchParams.set('cdn', 'akm');
                // parsedUrl.searchParams.set('ssai', 'true');
                this._cdnAdsUrlParams.forEach((param) => {
                    parsedUrl.searchParams.set(param.key, param.value);
                });
            }

            if (playerSSAIManager.canRunSSAI) await playerSSAIManager.addSSAIParamsToCDNHandlerURL(parsedUrl);

            // Format URL for SmartLib
            const formattedUrl = parsedUrl.toString();
            this._requestUrl = formattedUrl;

            try {
                const resolvedURL = await (this as typeof cdnHandler)._resolveSmartLib(formattedUrl, callback, session);

                // Set CDN name based on resolved URL
                this._cdnName = this.getCDNName(resolvedURL);
                this._resolvedsmartlibUrl = resolvedURL;

                return resolvedURL;
            } catch (error) {
                // Log the error and return original URL as fallback
                this._resolvedsmartlibUrl = '';
                cdnLog('resolve failed: falling back to the unresolved URL', {
                    url: shortUrl(formattedUrl),
                    error: (error as Error)?.message,
                });
                return formattedUrl;
            }
        });
        // The caller gets this resolve's own outcome, but the queue only waits for it to settle:
        // a failed link left at the tail would make every later `.then` skip its work and reject
        // with this error until the page is reloaded.
        this._resolveQueue = resolution.catch(() => undefined);
        return resolution;
    },

    async resolveTrailerUrl(url: string, token?: string, callback?: () => void, session?: unknown): Promise<string> {
        return this._resolveUrlWithQueue(url, token, callback, session);
    },

    async resolveURL(url: string, token?: string, callback?: () => void, session?: unknown): Promise<string> {
        return this._resolveUrlWithQueue(url, token, callback, session);
    },

    isSessionAlive(session: any): boolean {
        return !!session && !session.stopped;
    },

    setupAdvertising(adsParameters: Record<string, string>, cdnAdsUrlParams: Record<string, string>[]): void {
        if (!this._session) {
            return;
        }

        if (this._session?.stopped) {
            return;
        }

        this._isAdsEnabled = true;

        this._session.activateAdvertising();

        this._cdnAdsUrlParams = cdnAdsUrlParams;

        Object.entries(adsParameters).forEach(([key, value]) => {
            this._session.setAdParameter(key, value);
        });
    },

    setupEventsForSSAI(): void {
        cdnHandler._session.activateAdvertising();
        cdnHandler._session.setAdDataListener({
            onAdData: (adList) => {
                playerSSAIManager.processAdList(adList);
            },
        });
    },

    setupAdvertisingEvents(adsEvents: IAdEvents): void {
        if (!this.isSessionAlive(this._session)) return;

        this._session.setAdEventsListener({
            /**
             * Triggered when an ad break begin
             * @param adBreakData Ad break data
             */
            onAdBreakBegin: (adBreakData) => {
                // Fire ad break begin event
                npawPlugin?.getAdsAdapter()?.fireBreakStart(adBreakData);

                // Lock player controls
                adsEvents.onAdBreakBegin?.(adBreakData);
            },

            /**
             * Triggered when an ad begin
             * @param adData Ad data
             * @param adBreakData Ad break data
             */
            onAdBegin: (adData, adBreakData) => {
                // Add custom title = creativeId
                npawPlugin?.setAnalyticsOptions({
                    'ad.title': adData?.creativeId ?? 'NA',
                });

                // // Fire ad begin event
                npawPlugin?.getAdsAdapter()?.fireStart(adData, adBreakData);
                npawPlugin?.getAdsAdapter()?.fireJoin(adData, adBreakData);

                // Show ad link button if needed
                adsEvents.onAdBegin?.(adData, adBreakData);
            },

            /**
             * Triggered when an ad is skippable
             * @param adData Ad data
             * @param adBreakData Ad break data
             * @param adSkippablePosition position in millis where skip becomes allowed
             * @param adEndPosition position in millis of ad end
             * @param adBreakEndPosition position in millis of ad break end
             */
            onAdSkippable: (adData, adBreakData, adSkippablePosition, adEndPosition, adBreakEndPosition) => {
                // Show the skip message/button "skip ad in x seconds"
                adsEvents.onAdSkippable?.(adData, adBreakData, adSkippablePosition, adEndPosition, adBreakEndPosition);
            },

            /**
             * Triggered when the ad is ended, not called if skipped
             * @param adData Ad data
             * @param adBreakData Ad break data
             */
            onAdEnd: (adData, adBreakData) => {
                // Fire ad end event
                npawPlugin?.getAdsAdapter()?.fireStop(adData, adBreakData);

                // Hide ad link, hide ad skip button
                adsEvents.onAdEnd?.(adData, adBreakData);
            },

            /**
             * Triggered when ad break ended, even in case of skipping
             * @param adBreakData Ad break data
             */
            onAdBreakEnd: (adBreakData) => {
                // Fire ad break end event
                npawPlugin.getAdsAdapter()?.fireBreakStop(adBreakData);
                npawPlugin.getAdapter()?.fireJoin();

                // Unlock player controls, hide ad link, hide ad skip button
                adsEvents.onAdBreakEnd?.(adBreakData);
            },
        });
        this._session.setAdDataListener({
            /**
             * Triggered when ad data have been received during getURL
             * @param adList list of ad breaks
             */
            onAdData: (adList) => {
                adsEvents.onAdData?.(adList);
                void adList;
            },
        });
    },

    /**
     * Function to initialize SmartLib.
     * @param config
     */
    async init(config: any): Promise<void> {
        // await loadSmartLib();

        this.config = h.getCDNConfig(config);
        cdnLog('init', {
            enabled: !!this.config?.enabled,
            smartLibInitialised: this._initialized,
            current: describeSession(this._session),
            retired: this._retired.size,
        });

        // Never stop the current session here: `init` runs when a player component mounts, and the
        // session may belong to a player that is still streaming (hero trailer, minimised player).
        // Sessions are stopped by the player that owns them (`stopCDNSessionFor`).
        if (!this._session) {
            this._numberOfSessions = 0;
            this._player = null;
            this._isAdsEnabled = false;
        }

        if (this._initialized) {
            return;
        }
        if (!this.config) return;

        const userContext = user.getUserEnvironmentContext() || '';
        const { logLevel = SMARTLIB_LOG_LEVEL.DEBUG, umbrellaCDNHostName, override } = this.config || {};
        const { userEnvContexts, enable: overrideContext } = override || {};
        const overrideUserContext =
            userEnvContexts.find((item: string) => item.toLowerCase() === userContext.toLowerCase()) || '';
        if (overrideUserContext) {
            this.config.enabled = overrideContext;
        }
        if (!this.config.enabled) {
            return;
        }
        LoggerManager.getInstance().setLogLevel(logLevel);

        this.avodConfig = getAvodConfiguration();

        const preferredLinearParams = config?.videoCodec?.linear?.preferred;
        if (preferredLinearParams?.lowLatencyEnabled) {
            this._lowLatencyParams = preferredLinearParams?.lowLatencyParams ?? '';
        }

        SmartLib.getInstance().init(
            this.avodConfig.analyticsAddress,
            '',
            umbrellaCDNHostName || 'uselector.cdn.intigral.net'
        );
        SmartLib.getInstance().setOption(
            StreamingSessionOptions.SESSION_REPORTING_MODE,
            StreamingSessionOptions.SESSION_REPORTING_MODE_METRICS_RECEIVER
        );
        this._applyKeepaliveFrequency();
        this._initialized = true;
    },

    /**
     * Sets SmartLib's (global) keepalive frequency from the config. It used to be set again after
     * every `createStreamingSession()`; now it is set before a session is created, and only when the
     * configured value changes.
     */
    _applyKeepaliveFrequency(): void {
        const { keepaliveFrequency = 5000 } = this.config || {};
        if (this._appliedKeepaliveFrequency === keepaliveFrequency) return;

        SmartLib.getInstance().setOption(StreamingSessionOptions.SESSION_KEEPALIVE_FREQUENCY, keepaliveFrequency);
        this._appliedKeepaliveFrequency = keepaliveFrequency;
        cdnLog('keepalive frequency', { ms: keepaliveFrequency });
    },

    /**
     * Function to bind events to the player.
     */
    bindEvents(
        attachEvents: (events: PlayerEvent[], callback: AttachPlayerEventsCallback) => () => void,
        playerInstance?: PlayerAPI | null
    ): void {
        const target = playerInstance ?? player.getPlayer();
        if (!target) return;

        // One binding per player: binding the overlay's player used to unbind the hero's.
        this.destroyBoundEvents(target);
        const unbind = attachEvents(
            [
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'playing',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'paused',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'seeked',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'timeshifted',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'seeking',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'playbackfinished',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'error',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'unload',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'destroy',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'sourceloaded',
                // @ts-expect-error: Property 'on' does not exist on type 'Player'.
                'stallended',
            ],
            (lastEvent, event, e, firstPlay) => {
                this._onPlaybackStateChanged(lastEvent, event, e, firstPlay, target);
                // A destroyed player cannot fire again: drop its entry so the map does not hold it.
                if ((event as unknown as string) === 'destroy') this._boundEvents.delete(target);
            }
        );
        this._boundEvents.set(target, unbind);
    },

    /**
     * Removes the playback-event bindings of `playerInstance` (other players keep theirs), or of
     * every player when called without one.
     */
    destroyBoundEvents(playerInstance?: PlayerAPI | null): void {
        const players = playerInstance ? [playerInstance] : [...this._boundEvents.keys()];
        players.forEach((bound) => {
            const unbind = this._boundEvents.get(bound);
            if (!unbind) return;
            this._boundEvents.delete(bound);
            try {
                unbind();
            } catch (err) {
                // Unbinding from a player that is already destroyed is harmless.
                if (!isApiNotAvailableError(err)) console.error('[CDNHandler] - Error unbinding player events:', err);
            }
        });
    },

    /**
     * Function to start CDN session.
     */
    attachPlayerToCDNSession(playerInstance: PlayerAPI, session?: unknown): void {
        if (!playerInstance || !isPlayerAlive(playerInstance)) return;
        if (!this.config?.enabled) return;

        // The caller's own session may have been displaced by another player meanwhile: bind to it
        // where it is instead of to whichever session is current.
        const retired = session ? this._retired.get(session) : undefined;
        if (retired) {
            if (this.isSessionAlive(session)) (session as any).attachPlayer(playerInstance);
            retired.player = playerInstance;
            cdnLog('attach (to its displaced session)', {
                player: describePlayer(playerInstance),
                session: describeSession(session),
            });
            return;
        }

        this._player = playerInstance;
        cdnLog('attach', { player: describePlayer(playerInstance), session: describeSession(this._session) });

        if (this.isSessionAlive(this._session)) {
            this._session.attachPlayer(playerInstance);
        }
    },

    /**
     * Stops one SmartLib session, tolerating a player that has already been destroyed.
     */
    _stopSmartLibSession(session: any, playerInstance: PlayerAPI | null, reason = 'unspecified'): void {
        if (!session || session.stopped) return;

        noteSessionStopped(playerInstance);
        cdnLog(`stop ${describeSession(session)}: ${reason}`, {
            player: describePlayer(playerInstance),
            current: session === this._session,
        });
        try {
            session.stopStreamingSession();
        } catch (err) {
            // SmartLib detaches its listeners with `player.off(...)`; if the player was already
            // destroyed that is expected and harmless (guarded players never reach this branch).
            if (isApiNotAvailableError(err)) {
                markPlayerDestroyed(playerInstance);
            } else {
                console.error('[CDNHandler] - Error during SmartLib session stop:', err);
            }
        }
    },

    /**
     * Stops retired sessions whose player has been destroyed, and ones that never got a player
     * within `RETIRED_UNBOUND_TTL_MS` (their owner gave up before attaching).
     */
    _sweepRetiredSessions(): void {
        const now = Date.now();
        this._retired.forEach(({ player: owner, since }, session) => {
            const ownerGone = owner ? !isPlayerAlive(owner) : now - since > RETIRED_UNBOUND_TTL_MS;
            if (!ownerGone && this.isSessionAlive(session)) return;

            this._stopSmartLibSession(
                session,
                owner,
                owner ? 'displaced session, its player is gone' : 'displaced session never got a player (60s)'
            );
            this._retired.delete(session);
            this._numberOfSessions = Math.max(0, this._numberOfSessions - 1);
        });
    },

    /**
     * Function to stop CDN session.
     */
    stopCDNSession(reason = 'unspecified'): void {
        const session = this._session;
        if (!this.config?.enabled || !session || session.stopped) return;

        try {
            this._stopSmartLibSession(session, this._player, reason);
        } finally {
            // Always clean up local state, even if stop fails
            this._session = null;
            this._player = null;
            this._isAdsEnabled = false;
            this._cdnAdsUrlParams = [];
            this._numberOfSessions = Math.max(0, this._numberOfSessions - 1);
        }
    },

    /**
     * Stops the CDN session only if the caller owns it: it is bound to `playerInstance`, or it is
     * `session` (the one the caller started with `initiateSession`, before any player is attached).
     * Meant to be called right before that player is destroyed, so SmartLib can detach its
     * listeners while the player API is still alive. A session started for, or bound to, another
     * player is left untouched — including a new one that has no player attached yet.
     */
    stopCDNSessionFor(playerInstance: PlayerAPI | null | undefined, session?: unknown, reason = 'owner stop'): void {
        if (session && typeof session === 'object') this._abandoned.add(session);

        let stoppedRetired = false;
        this._retired.forEach(({ player: owner }, retiredSession) => {
            const owns = (!!playerInstance && owner === playerInstance) || (!!session && retiredSession === session);
            if (!owns) return;

            this._stopSmartLibSession(retiredSession, owner, `${reason} (displaced session)`);
            this._retired.delete(retiredSession);
            this._numberOfSessions = Math.max(0, this._numberOfSessions - 1);
            stoppedRetired = true;
        });

        const ownsPlayer = !!playerInstance && this._player === playerInstance;
        const ownsSession = !!session && this._session === session;
        if (!ownsPlayer && !ownsSession) {
            const alreadyStopped = (session as any)?.stopped || wasSessionStopped(playerInstance);
            if (!stoppedRetired && alreadyStopped) {
                cdnLog(`stop: nothing to do, already stopped (${reason})`, {
                    player: describePlayer(playerInstance),
                    session: describeSession(session),
                });
            } else if (!stoppedRetired) {
                cdnLog(`stop ignored: caller does not own the current session (${reason})`, {
                    caller: { player: describePlayer(playerInstance), session: describeSession(session) },
                    current: { session: describeSession(this._session), player: describePlayer(this._player) },
                });
            }
            return;
        }

        this.stopCDNSession(reason);
    },

    /**
     * Stops the CDN session when the player it is bound to has been destroyed.
     * Safe to call from a `PlayerEvent.Destroy` handler: a session bound to a newer, live player
     * instance (e.g. Live TV channel switch) is kept, and so is one with no player attached yet —
     * that is a new session waiting for its player, not one left behind by a dead one.
     */
    stopCDNSessionIfPlayerGone(): void {
        this._sweepRetiredSessions();
        if (!this._player || isPlayerAlive(this._player)) return;

        this.stopCDNSession('its player was destroyed');
    },

    /**
     * Function to resume CDN session.
     */
    resumeCDNSession(): void {
        if (!this.config || !this.config.enabled) {
            return;
        }

        PlayerEventListener.notifyResume();
    },

    /**
     * Function to get CDN name from URL.
     * @param path
     * @returns {string}
     */
    getCDNName(path: string): string {
        // Derived from each resolved URL; caching the first one reported a stale CDN for later streams.
        if (!path) return '';
        const subDomain = url.getSubDomain(path);
        if (this.isBroadPeakCDN(subDomain)) {
            return CDN.BROADPEAK;
        }
        return CDN.AKAMAI;
    },

    /**
     * Function to check if the CDN is BroadPeak.
     * @param subDomain
     * @returns {boolean}
     */
    isBroadPeakCDN(subDomain: string): boolean {
        const { broadpeakSubdomains } = this.config;
        if (!broadpeakSubdomains || !broadpeakSubdomains.length) return false;
        return broadpeakSubdomains.some((cdn: string) => subDomain.indexOf(cdn) > -1);
    },

    /**
     * Starts a new SmartLib session and makes it the current one.
     * @param previous The caller's own earlier session (e.g. before a channel or episode switch). It
     * is stopped. Any other live session is retired, not stopped: another player may still be
     * streaming from it (the hero trailer behind a detail overlay), and its own player stops it.
     * @returns The new session, so the caller can attach to and stop exactly this session.
     */
    initiateSession(previous?: unknown) {
        if (previous) this.stopCDNSessionFor(null, previous, 'replaced by its owner (channel / episode switch)');
        this._sweepRetiredSessions();

        if (this.isSessionAlive(this._session)) {
            if (this._player && !isPlayerAlive(this._player)) {
                // Its player is gone: nobody streams from it any more.
                this.stopCDNSession('replaced, its player is gone');
            } else {
                cdnLog(`retire ${describeSession(this._session)}: another player is starting`, {
                    player: describePlayer(this._player),
                    note: this._player
                        ? 'kept running for its player'
                        : 'no player attached yet; swept after 60s if none attaches',
                });
                // A session with no player yet is either still being set up by its owner or was
                // left behind by one that unmounted mid-resolve; check back once the grace period
                // is over so the abandoned kind does not keep sending keepalives indefinitely.
                if (!this._player) setTimeout(() => this._sweepRetiredSessions(), RETIRED_UNBOUND_TTL_MS + 1000);
                this._retired.set(this._session, { player: this._player, since: Date.now() });
                this._session = null;
                this._player = null;
                this._isAdsEnabled = false;
                this._cdnAdsUrlParams = [];
            }
        }

        // Create a new SmartLib session (keepalive frequency first, so it applies to this session)
        this._applyKeepaliveFrequency();
        this._session = SmartLib.getInstance().createStreamingSession();
        this._numberOfSessions++;
        cdnLog(`create ${describeSession(this._session)}`, {
            previous: previous ? describeSession(previous) : 'none',
            retired: this._retired.size,
            sessions: this._numberOfSessions,
        });
        return this._session;
    },

    /**
     * Resolves a URL using SmartLib
     * @param url The URL to resolve
     * @returns The resolved URL from SmartLib
     */
    async _resolveSmartLib(url: string, callback?: () => void, ownSession?: unknown): Promise<string> {
        // Configure SmartLib session
        // const { keepaliveFrequency = 5000 } = this.config || {};
        // // this.stopCDNSession();
        // SmartLib.getInstance().setOption(StreamingSessionOptions.SESSION_KEEPALIVE_FREQUENCY, keepaliveFrequency);

        // this.initiateSession();

        const session: any = ownSession ?? this._session;
        if (!this.isSessionAlive(session)) {
            throw new Error('[CDNHandler] SmartLib session is not alive');
        }

        try {
            // Get resolved URL
            cdnLog(`getURL on ${describeSession(session)}`, {
                url: shortUrl(url),
                current: session === this._session,
            });
            const result = await session.getURL(url);
            if (!result.isError()) {
                rememberSessionToken(session, result.getURL());
                cdnLog(`getURL ok ${describeSession(session)}`, { resolved: shortUrl(result.getURL()) });
                return result.getURL();
            }
            cdnLog(`getURL error on ${describeSession(session)}`, {
                code: result.getErrorCode?.(),
                hint: result.getErrorCode?.() === 3403 ? 'session already resolved a URL (reused session)' : undefined,
            });
            throw new Error(`Failed to resolve URL with SmartLib for ${url} (code ${result.getErrorCode?.()})`);
        } catch (error) {
            // A failed resolve must never tear down a session a player is streaming from. The usual
            // case is SmartLib 3403 (`REQUEST_ALREADY_DONE`): the session already resolved the URL
            // the attached player is playing, and stopping it makes the CDN reject every further
            // segment with 403. A session nobody plays from is stopped so the next resolve starts a
            // fresh one instead of hitting 3403 again.
            const retired = this._retired.get(session);
            if (retired && !isPlayerAlive(retired.player)) {
                this._stopSmartLibSession(session, null, 'resolve failed, no player on it');
                this._retired.delete(session);
                this._numberOfSessions = Math.max(0, this._numberOfSessions - 1);
            } else if (this._session === session && !isPlayerAlive(this._player)) {
                this.stopCDNSession('resolve failed, no player on it');
            } else {
                cdnLog(`kept ${describeSession(session)} after failed resolve: a live player streams from it`, {
                    player: describePlayer(retired ? retired.player : this._player),
                });
            }
            throw error;
        }
    },

    /**
     * Function to handle playback state changes.
     * @param prevEvent
     * @param currEvent
     * @param e
     * @param firstPlay
     */
    _onPlaybackStateChanged(
        prevEvent = '',
        currEvent: string,
        e: any,
        firstPlay: boolean,
        playerInstance?: PlayerAPI | null
    ): void {
        switch (currEvent) {
            case 'unload':
            case 'playbackfinished':
            case 'error':
            case 'destroy':
                // Only the session of the player that raised the event, never a newer player's.
                this.stopCDNSessionFor(playerInstance, undefined, `player event: ${currEvent}`);
        }
    },
};

registerCdnDebugState(() => ({
    enabled: !!cdnHandler.config?.enabled,
    current: {
        session: describeSession(cdnHandler._session),
        player: describePlayer(cdnHandler._player),
        playerAlive: isPlayerAlive(cdnHandler._player),
    },
    retired: [...cdnHandler._retired.entries()].map(([session, { player: owner, since }]) => ({
        session: describeSession(session),
        player: describePlayer(owner),
        playerAlive: isPlayerAlive(owner),
        ageMs: Date.now() - since,
    })),
    sessions: cdnHandler._numberOfSessions,
    cdn: cdnHandler._cdnName,
    boundPlayers: [...cdnHandler._boundEvents.keys()].map((bound) => describePlayer(bound)),
}));

export default cdnHandler;
export type { IAdBreakData, IAdData };
export { retryN };
